import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  Landmark, 
  Truck, 
  Wrench,
  Droplet,
  Gauge,
  Building2,
  Cpu,
  Calculator,
  Dumbbell,
  UtensilsCrossed,
  ArrowRight
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const industries = [
  {
    id: 'logistics',
    icon: Truck,
    name: 'Logistics',
    description: 'Streamline supply chain operations with real-time tracking and data integration.',
    stats: { dataPoints: '420M', revenueLift: '36%' },
    color: 'from-indigo-500 to-violet-500',
  },
  {
    id: 'testandbalance',
    icon: Gauge,
    name: 'Test and Balance',
    description: 'Automate testing processes and balance reporting for HVAC systems.',
    stats: { dataPoints: '120M', revenueLift: '28%' },
    color: 'from-orange-500 to-amber-500',
  },
  {
    id: 'maintenance',
    icon: Wrench,
    name: 'Maintenance',
    description: 'Predictive maintenance solutions and facility management platforms.',
    stats: { dataPoints: '650M', revenueLift: '41%' },
    color: 'from-emerald-500 to-teal-500',
  },
  {
    id: 'finance',
    icon: Landmark,
    name: 'Finance',
    description: 'Secure financial data processing and portfolio management systems.',
    stats: { dataPoints: '2.4B', revenueLift: '45%' },
    color: 'from-blue-500 to-indigo-500',
  },
  {
    id: 'realestate',
    icon: Building2,
    name: 'Real Estate',
    description: 'Property management platforms with automated reporting and tenant portals.',
    stats: { dataPoints: '320M', revenueLift: '33%' },
    color: 'from-sky-500 to-blue-500',
  },
  {
    id: 'plumbing',
    icon: Droplet,
    name: 'Plumbing',
    description: 'Service tracking and scheduling systems for plumbing operations.',
    stats: { dataPoints: '180M', revenueLift: '29%' },
    color: 'from-cyan-500 to-blue-500',
  },
  {
    id: 'accounting',
    icon: Calculator,
    name: 'Accounting',
    description: 'Client portals with document management and financial reporting tools.',
    stats: { dataPoints: '280M', revenueLift: '32%' },
    color: 'from-green-500 to-emerald-500',
  },
  {
    id: 'sporting',
    icon: Dumbbell,
    name: 'Sporting',
    description: 'Community platforms and data analytics for sports organizations.',
    stats: { dataPoints: '150M', revenueLift: '27%' },
    color: 'from-red-500 to-rose-500',
  },
  {
    id: 'technology',
    icon: Cpu,
    name: 'Technology',
    description: 'Infrastructure solutions for tech companies and SaaS platforms.',
    stats: { dataPoints: '3.2B', revenueLift: '67%' },
    color: 'from-slate-500 to-gray-500',
  },
  {
    id: 'foodfranchises',
    icon: UtensilsCrossed,
    name: 'Food Franchises',
    description: 'Operations management and data insights for restaurant chains.',
    stats: { dataPoints: '890M', revenueLift: '38%' },
    color: 'from-orange-500 to-red-500',
  },
];

const Industries = () => {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [activeIndustry, setActiveIndustry] = useState<string | null>(null);

  // Map industries to their primary client
  const industryToClient: Record<string, string> = {
    'logistics': 'mobispaw',
    'testandbalance': 'smartab',
    'maintenance': 'the-national-fix',
    'finance': 'viridia-capital',
    'realestate': 'priveda-capital',
    'plumbing': 'levine-bros',
    'accounting': 'cyg-finance',
    'sporting': 'spikeball',
    'technology': 'chatzy',
    'foodfranchises': 'mobispaw', // Default to mobispaw if no specific mapping
  };

  const handleIndustryClick = (industryId: string) => {
    const clientSlug = industryToClient[industryId];
    if (clientSlug) {
      navigate(`/client/${clientSlug}`);
    }
  };

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    
    // Skip ScrollTrigger animations on mobile for better performance
    if (isMobile) {
      // Just set elements to visible state immediately on mobile
      if (titleRef.current) {
        gsap.set(titleRef.current, { opacity: 1, y: 0 });
      }
      if (imageRef.current) {
        gsap.set(imageRef.current, { opacity: 1, x: 0 });
      }
      const cards = gridRef.current?.querySelectorAll('.industry-card');
      if (cards) {
        gsap.set(cards, { opacity: 1, y: 0 });
      }
      return;
    }
    
    const ctx = gsap.context(() => {
      // Set initial states first to prevent flashing
      if (titleRef.current) gsap.set(titleRef.current, { opacity: 1, y: 0 });
      if (imageRef.current) gsap.set(imageRef.current, { opacity: 1, x: 0 });
      
      // Title animation - desktop only
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: 'expo.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 85%',
            once: true, // Only animate once - prevents flashing
            toggleActions: 'play none none none', // Never reverse
          },
        }
      );

      // Image animation - desktop only
      gsap.fromTo(
        imageRef.current,
        { opacity: 0, x: -50 },
        {
          opacity: 1,
          x: 0,
          duration: 0.8,
          ease: 'expo.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 80%',
            once: true, // Only animate once - prevents flashing
            toggleActions: 'play none none none', // Never reverse
          },
        }
      );

      // Grid animation - desktop only
      const cards = gridRef.current?.querySelectorAll('.industry-card');
      if (cards) {
        // Set initial state first
        gsap.set(cards, { opacity: 1, y: 0 });
        
        gsap.fromTo(
          cards,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
            stagger: 0.08,
            ease: 'expo.out',
            scrollTrigger: {
              trigger: gridRef.current,
              start: 'top 85%',
              once: true, // Only animate once - prevents flashing
              toggleActions: 'play none none none', // Never reverse
            },
          }
        );
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="industries"
      ref={sectionRef}
      className="relative py-32 bg-kmg-bg overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-kmg-primary/5 rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div ref={titleRef} className="text-center mb-16">
          <span className="inline-block px-4 py-2 text-sm font-medium text-kmg-primary bg-kmg-primary/10 rounded-full mb-6">
            Industry Coverage
          </span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Industry <span className="text-gradient">Intelligence</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Data doesn't exist in silos. KMG's cross-industry analytics reveal patterns 
            and opportunities that single-sector tools simply cannot see.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12 mb-16">
          {/* Image - takes 2 columns */}
          <div ref={imageRef} className="lg:col-span-2">
            <div className="sticky top-32">
              <img
                src="/aboutUs.png"
                alt="KMG Vectoral System"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>

          {/* Industries Grid - takes 3 columns */}
          <div ref={gridRef} className="lg:col-span-3">
            <div className="grid sm:grid-cols-2 gap-4">
              {industries.map((industry) => {
                const Icon = industry.icon;
                const isActive = activeIndustry === industry.id;
                
                return (
                  <div
                    key={industry.id}
                    className={`industry-card group relative p-5 bg-white rounded-xl shadow-card hover:shadow-card-hover transition-all duration-300 cursor-pointer ${
                      isActive ? 'ring-2 ring-kmg-primary' : ''
                    }`}
                    onMouseEnter={() => setActiveIndustry(industry.id)}
                    onMouseLeave={() => setActiveIndustry(null)}
                    onClick={() => handleIndustryClick(industry.id)}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 flex items-center justify-center rounded-lg bg-gradient-to-br ${industry.color} flex-shrink-0`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-slate-900">{industry.name}</h4>
                          <ArrowRight className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                        </div>
                        <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                          {industry.description}
                        </p>
                        <div className="flex gap-4 text-xs">
                          <span className="text-slate-500">
                            <strong className="text-kmg-primary">{industry.stats.dataPoints}</strong> data points
                          </span>
                          <span className="text-slate-500">
                            <strong className="text-green-600">{industry.stats.revenueLift}</strong> revenue lift
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Cross-Industry Benefits */}
        <div className="bg-gradient-to-r from-kmg-primary to-kmg-accent rounded-2xl p-8 md:p-12 text-white">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <p className="text-4xl font-bold mb-2">{industries.length}+</p>
              <p className="text-white/80">Industries Covered</p>
            </div>
            <div>
              <p className="text-4xl font-bold mb-2">10B+</p>
              <p className="text-white/80">Cross-Referenced Data Points</p>
            </div>
            <div>
              <p className="text-4xl font-bold mb-2">43%</p>
              <p className="text-white/80">Avg. Insight Improvement</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Industries;
